/* eslint-disable import/no-extraneous-dependencies */
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TRANSFORMERS } from '@lexical/markdown';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { addClassNamesToElement, isHTMLElement } from '@lexical/utils';

import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $isTextNode,
  TextNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type EditorState,
  type LexicalEditor,
  type LexicalNode,
  type SerializedElementNode,
  type SerializedTextNode,
  type Spread,
} from 'lexical';

import ToolbarPlugin from './plugins/toolbar-plugin';
import ExampleTheme from './themes';
import './styles.css';

import ListMaxIndentLevelPlugin from './plugins/list-max-indent-level-plugin';
import AutoLinkPlugin from './plugins/auto-link-plugin';

import { registerCodeHighlighting } from '@lexical/code';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef, useState } from 'react';
import PageBreakPlugin from './plugins/page-break-plugin';
import { PageBreakNode } from './nodes/page-break-node';
import TableCellActionMenuPlugin from './plugins/table-action-menu-plugin';
import TableCellResizer from './plugins/table-cell-resizer';
import ImagesPlugin from './plugins/images-plugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { ImageNode } from './nodes/image-node';
import { YouTubeNode } from './nodes/you-tube-node';
import { FigmaNode } from './nodes/figma-node';
import FigmaPlugin from './plugins/figma-plugin';
import YouTubePlugin from './plugins/you-tube-plugin';
import AutoEmbedPlugin from './plugins/auto-embed-plugin';
import DraggableBlockPlugin from './plugins/draggable-block-plugin';
import InlineImagePlugin from './plugins/inline-image-plugin';
import { InlineImageNode } from './nodes/inline-image-node';
import { LayoutItemNode } from './nodes/layout-item-node';
import { LayoutContainerNode } from './nodes/layout-container-node';
import LayoutPlugin from './plugins/layout-plugin';
import CollapsiblePlugin from './plugins/collapsible-plugin';
import { CollapsibleContainerNode } from './nodes/collapsible-container-node';
import { CollapsibleTitleNode } from './nodes/collapsible-title-node';
import { CollapsibleContentNode } from './nodes/collapsible-content-node';
import { StickyNode } from './nodes/sticky-node';
import FloatingTextFormatToolbarPlugin from './plugins/floating-text-format-toolbar-plugin';
import ActionsPlugin from './plugins/actions-plugin';
// import { TextStyleNode } from '@lexical/text'

import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

function CodeHighlightPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return registerCodeHighlighting(editor);
  }, [editor]);

  return null;
}

export function Placeholder({ placeholder, className }: { placeholder?: string; className?: string }) {
  return <div className={cn("editor-placeholder", className)}>{placeholder}</div>;
}

export type SerializedCustomHeadingNode = Spread<
  {
    tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  },
  SerializedElementNode
>;

export class CustomTextNode extends TextNode {
  static getType(): string {
    return 'custom-text';
  }

  static clone(node: CustomTextNode): CustomTextNode {
    return new CustomTextNode(node.__text, node.__key);
  }

  static importDOM(): DOMConversionMap | null {
    const baseConversion = super.importDOM();

    // Lexical formatting bitmasks
    const IS_BOLD = 1;
    const IS_ITALIC = 2;
    const IS_STRIKETHROUGH = 4;
    const IS_UNDERLINE = 8;
    const IS_CODE = 16;

    const convertWithStyleAndFormat = (element: HTMLElement) => {
      const style = element.style.cssText;
      const tagName = element.tagName.toLowerCase();

      return {
        forChild: (lexicalNode: LexicalNode) => {
          if ($isTextNode(lexicalNode)) {
            if (style) {
              lexicalNode.setStyle(style);
            }

            // Apply Lexical formats based on the parent HTML tag
            let format = lexicalNode.getFormat();
            if (tagName === 'strong' || tagName === 'b') {
              format |= IS_BOLD;
            } else if (tagName === 'em' || tagName === 'i') {
              format |= IS_ITALIC;
            } else if (tagName === 'u') {
              format |= IS_UNDERLINE;
            } else if (tagName === 's' || tagName === 'strike') {
              format |= IS_STRIKETHROUGH;
            } else if (tagName === 'code') {
              format |= IS_CODE;
            }

            if (format !== lexicalNode.getFormat()) {
              lexicalNode.setFormat(format);
            }
          }
          return lexicalNode;
        },
        node: null,
      };
    };

    return {
      ...baseConversion,
      span: (_node: Node) => ({
        conversion: convertWithStyleAndFormat,
        priority: 1,
      }),
      strong: (_node: Node) => ({
        conversion: convertWithStyleAndFormat,
        priority: 1,
      }),
      b: (_node: Node) => ({
        conversion: convertWithStyleAndFormat,
        priority: 1,
      }),
      em: (_node: Node) => ({
        conversion: convertWithStyleAndFormat,
        priority: 1,
      }),
      i: (_node: Node) => ({
        conversion: convertWithStyleAndFormat,
        priority: 1,
      }),
      u: (_node: Node) => ({
        conversion: convertWithStyleAndFormat,
        priority: 1,
      }),
      s: (_node: Node) => ({
        conversion: convertWithStyleAndFormat,
        priority: 1,
      }),
      strike: (_node: Node) => ({
        conversion: convertWithStyleAndFormat,
        priority: 1,
      }),
      code: (_node: Node) => ({
        conversion: convertWithStyleAndFormat,
        priority: 1,
      }),
    };
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const { element } = super.exportDOM(editor);
    const style = this.getStyle();

    if (style && isHTMLElement(element)) {
      const span = document.createElement('span');
      span.style.cssText = style;
      span.appendChild(element);
      return { element: span };
    }

    return { element };
  }

  static importJSON(serializedNode: SerializedTextNode): CustomTextNode {
    const node = $createCustomTextNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  exportJSON(): SerializedTextNode {
    return {
      ...super.exportJSON(),
      type: 'custom-text',
      version: 1,
    };
  }
}

export function $createCustomTextNode(text: string): CustomTextNode {
  return new CustomTextNode(text);
}

export function $isCustomTextNode(
  node: LexicalNode | null | undefined
): node is CustomTextNode {
  return node instanceof CustomTextNode;
}

export class CustomHeadingNode extends HeadingNode {
  static getType(): string {
    return 'custom-heading';
  }

  static clone(node: CustomHeadingNode): CustomHeadingNode {
    return new CustomHeadingNode(node.__tag, node.__key);
  }

  getTag(): 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' {
    return this.__tag;
  }

  // View

  createDOM(config: EditorConfig): HTMLElement {
    const tag = this.__tag;
    const element = document.createElement(tag);
    const theme = config.theme;
    const classNames = theme.heading;
    if (classNames !== undefined) {
      const className = classNames[tag as keyof typeof classNames];
      if (className) {
        addClassNamesToElement(element, className);
      }
    }
    addClassNamesToElement(element, 'scroll-mt-32');
    element.id = this.getTextContent().replace(/\s+/g, '-').toLowerCase();
    return element;
  }

  updateDOM(_prevNode: CustomHeadingNode, _dom: HTMLElement): boolean {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      h1: (_node: Node) => ({
        conversion: convertHeadingElement,
        priority: 0,
      }),
      h2: (_node: Node) => ({
        conversion: convertHeadingElement,
        priority: 0,
      }),
      h3: (_node: Node) => ({
        conversion: convertHeadingElement,
        priority: 0,
      }),
      h4: (_node: Node) => ({
        conversion: convertHeadingElement,
        priority: 0,
      }),
      h5: (_node: Node) => ({
        conversion: convertHeadingElement,
        priority: 0,
      }),
      h6: (_node: Node) => ({
        conversion: convertHeadingElement,
        priority: 0,
      }),
    };
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const { element } = super.exportDOM(editor);

    if (element && isHTMLElement(element)) {
      if (this.isEmpty()) {
        element.append(document.createElement('br'));
      }

      const formatType = this.getFormatType();
      element.style.textAlign = formatType;

      const direction = this.getDirection();
      if (direction) {
        element.dir = direction;
      }

      const style = this.getStyle();
      if (style) {
        element.style.cssText = style;
      }
    }

    return {
      element: element as HTMLElement,
    };
  }

  static importJSON(
    serializedNode: SerializedCustomHeadingNode
  ): CustomHeadingNode {
    const node = $createCustomHeadingNode(serializedNode.tag);
    node.setFormat(serializedNode.format);
    node.setIndent(serializedNode.indent);
    node.setDirection(serializedNode.direction);
    return node;
  }

  exportJSON(): SerializedCustomHeadingNode {
    return {
      ...super.exportJSON(),
      tag: this.getTag(),
      type: 'custom-heading',
      version: 1,
    };
  }
}

function convertHeadingElement(element: HTMLElement): DOMConversionOutput {
  const nodeName = element.nodeName.toLowerCase();
  let node = null;
  if (
    nodeName === 'h1' ||
    nodeName === 'h2' ||
    nodeName === 'h3' ||
    nodeName === 'h4' ||
    nodeName === 'h5' ||
    nodeName === 'h6'
  ) {
    node = $createCustomHeadingNode(nodeName as any);
    if (element.style !== null) {
      node.setFormat(element.style.textAlign as any);
      node.setStyle(element.style.cssText);
    }
  }
  return { node };
}

export function $createCustomHeadingNode(
  tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
): CustomHeadingNode {
  return new CustomHeadingNode(tag);
}

export function $isCustomHeadingNode(
  node: LexicalNode | null | undefined
): node is CustomHeadingNode {
  return node instanceof CustomHeadingNode;
}

export interface EditorProps {
  placeholder?: string;
  initialContent?: string | null; // Now HTML instead of JSON
  editable?: boolean;
  storeText?: (html: string, isEmpty: boolean) => void; // Now HTML instead of JSON
  style?: string;
  editorKey?: string;
  toolbarstyle?: string;
  chatRich?: boolean;
  handleActionClick?: (action: string) => void;
  onUploadFile?: (file: File) => Promise<string>;
  onProvideGetHtml?: (getHtml: () => string) => void;
  onProvideGetSerializedState?: (getSerializedState: () => string) => void;
  children?: React.ReactNode;
}

function EditorContent({
  placeholder,
  editable,
  storeText,
  style,
  toolbarstyle,
  chatRich,
  handleActionClick,
  onUploadFile,
  children,
}: EditorProps) {
  const [floatingAnchorElem, setFloatingAnchorElem] =
    useState<HTMLElement | null>(null);
  const [_isLinkEditMode, setIsLinkEditMode] = useState(false);
  const [editor] = useLexicalComposerContext();

  const onChange = (editorState: EditorState) => {
    const isEditorEmpty = editorState.read(() => {
      const root = $getRoot();
      const firstChild = root.getFirstChild();
      const isEmpty =
        (!firstChild || firstChild.getTextContent() === '') &&
        root.getChildrenSize() === 1;
      return isEmpty;
    });

    editorState.read(() => {
      const html = $generateHtmlFromNodes(editor);
      storeText && storeText(html, isEditorEmpty);
    });
  };

  const onRef = (_floatingAnchorElem: HTMLElement | null) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  return (
    <div className="w-full editor-shell">
      {editable && (
        <ToolbarPlugin
          toolbarstyle={toolbarstyle}
          chatRich={chatRich}
          onUploadFile={onUploadFile}
        />
      )}
      <div>
        <RichTextPlugin
          contentEditable={
            <div className="editor-scroller">
              <div className="editor" ref={onRef}>
                <ContentEditable className={cn("editor-input outline-none text-foreground", style)} />
              </div>
            </div>
          }
          placeholder={<Placeholder placeholder={placeholder} className={cn(style?.includes('p-') && "top-[unset] left-[unset] static p-[inherit] opacity-50")} />}
          ErrorBoundary={LexicalErrorBoundary as any}
        />
        <HistoryPlugin />
        <AutoFocusPlugin />
        <ClearEditorPlugin />
        <CodeHighlightPlugin />
        <TablePlugin hasCellMerge={true} hasCellBackgroundColor={true} />
        <ListPlugin />
        <TabIndentationPlugin />
        <LinkPlugin />
        {!chatRich && <AutoEmbedPlugin />}
        <YouTubePlugin />
        {!chatRich && <FigmaPlugin />}
        {!chatRich && <TableCellResizer />}
        <AutoLinkPlugin />
        {!chatRich && <PageBreakPlugin />}
        {!chatRich && <HorizontalRulePlugin />}
        <ImagesPlugin />
        {!chatRich && <InlineImagePlugin />}
        {!chatRich && <CollapsiblePlugin />}
        {!chatRich && <LayoutPlugin />}
        {!chatRich && floatingAnchorElem && (
          <>
            <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
            <TableCellActionMenuPlugin
              anchorElem={floatingAnchorElem}
              cellMerge={true}
            />
            <FloatingTextFormatToolbarPlugin
              anchorElem={floatingAnchorElem}
              setIsLinkEditMode={setIsLinkEditMode}
            />
          </>
        )}
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
        <OnChangePlugin onChange={onChange} />
        {chatRich && handleActionClick && (
          <ActionsPlugin handleActionClick={handleActionClick as any} />
        )}
        {children}
      </div>
    </div>
  );
}

export default function Editor({
  placeholder,
  initialContent,
  editable,
  storeText,
  style,
  editorKey,
  toolbarstyle,
  chatRich,
  handleActionClick,
  onUploadFile,
  children,
}: EditorProps) {
  const getInitialContent = (content: string | null) => {
    if (content) {
      // Check if content is HTML (contains HTML tags) or JSON
      if (content.includes('<') && content.includes('>')) {
        // It's HTML, convert to JSON
        try {
          const dom = preprocessHtml(content);
          const tempEditor = {
            _config: {
              nodes: [
                CustomTextNode,
                {
                  replace: TextNode,
                  with: (node: TextNode) => {
                    const customNode = new CustomTextNode(
                      node.getTextContent()
                    );
                    customNode.setFormat(node.getFormat());
                    customNode.setStyle(node.getStyle());
                    return customNode;
                  },
                },
                ImageNode,
                InlineImageNode,
                ListNode,
                ListItemNode,
                QuoteNode,
                CodeNode,
                CodeHighlightNode,
                TableNode,
                TableCellNode,
                TableRowNode,
                AutoLinkNode,
                LinkNode,
                PageBreakNode,
                HorizontalRuleNode,
                YouTubeNode,
                FigmaNode,
                CustomHeadingNode,
                LayoutItemNode,
                LayoutContainerNode,
                CollapsibleContainerNode,
                CollapsibleTitleNode,
                CollapsibleContentNode,
                StickyNode,
                HeadingNode,
              ],
            },
          } as any;
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = dom.body.innerHTML;
          const nodes = $generateNodesFromDOM(tempEditor, tempDiv);
          const root = $createParagraphNode();
          for (const node of nodes) {
            if ($isElementNode(node)) {
              root.append(...node.getChildren());
            } else {
              root.append(node);
            }
          }
          const editorState = {
            read: (callback: any) => callback(),
            toJSON: () => ({ root: root.exportJSON() }),
          };
          return JSON.stringify(editorState.toJSON());
        } catch (e) {
          return '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';
        }
      } else {
        // It's JSON
        try {
          const jsonContent = JSON.parse(content);
          return jsonContent?.root?.children?.length > 0
            ? content
            : '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';
        } catch (e) {
          return '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';
        }
      }
    }
    return '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';
  };

  const editorConfig = {
    namespace: 'RichTextEditor',
    // The editor theme
    theme: ExampleTheme,
    editorState: getInitialContent(initialContent || null),
    editable,

    // Handling of errors during update
    onError(error: Error) {
      throw error;
    },
    // Any custom nodes go here
    nodes: [
      CustomTextNode,
      {
        replace: TextNode,
        with: (node: TextNode) => {
          const customNode = new CustomTextNode(node.getTextContent());
          customNode.setFormat(node.getFormat());
          customNode.setStyle(node.getStyle());
          return customNode;
        },
      },
      ImageNode,
      // TextStyleNode,
      InlineImageNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      TextNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      PageBreakNode,
      HorizontalRuleNode,
      YouTubeNode,
      FigmaNode,
      CustomHeadingNode,
      LayoutItemNode,
      LayoutContainerNode,
      CollapsibleContainerNode,
      CollapsibleTitleNode,
      CollapsibleContentNode,
      StickyNode,
      HeadingNode,
    ],
  };

  return (
    <LexicalComposer key={editorKey} initialConfig={editorConfig}>
      <EditorContent
        placeholder={placeholder}
        initialContent={initialContent}
        editable={editable}
        storeText={storeText}
        style={style}
        editorKey={editorKey}
        toolbarstyle={toolbarstyle}
        chatRich={chatRich}
        handleActionClick={handleActionClick}
        onUploadFile={onUploadFile}
        children={children}
      />
    </LexicalComposer>
  );
}
/**
 * Internal plugin to bridge the gap between RichText's internal
 * JSON-based state and the application's HTML-based state.
 */
function StateBridgePlugin({
  html,
  onChange,
  onProvideGetHtml,
  onProvideGetSerializedState,
}: {
  html?: string | null;
  onChange?: (html: string) => void;
  onProvideGetHtml?: (getHtml: () => string) => void;
  onProvideGetSerializedState?: (getSerializedState: () => string) => void;
}) {
  const [editor] = useLexicalComposerContext();

  const lastImportedRef = useRef<string>(''); // last html we imported into editor
  const lastExportedRef = useRef<string>(''); // last html we exported to parent
  const applyingExternalRef = useRef(false);
  const didInitRef = useRef(false);
  const isHydratedRef = useRef(false);

  // OUT: Editor -> HTML
  const executeOnChange = (editorState: EditorState) => {
    if (applyingExternalRef.current) return;
    // Ignore initial empty emissions until external value is hydrated.
    if (!isHydratedRef.current) return;
    if (editor.getRootElement() == null) return;

    editorState.read(() => {
      let newHtml = '';
      try {
        newHtml = $generateHtmlFromNodes(editor);
      } catch (e) {
        // don't crash on export
        return;
      }

      // Prevent initial hydration glitches from wiping a non-empty imported value.
      if (!newHtml.trim() && lastImportedRef.current.trim()) return;

      // prevent spam + prevent loop
      if (newHtml === lastExportedRef.current) return;
      lastExportedRef.current = newHtml;

      onChange?.(newHtml);
    });
  };

  // Provide synchronous getter
  useEffect(() => {
    onProvideGetHtml?.(() => {
      try {
        return $generateHtmlFromNodes(editor);
      } catch {
        return lastExportedRef.current;
      }
    });
  }, [editor, onProvideGetHtml]);

  useEffect(() => {
    onProvideGetSerializedState?.(() => {
      try {
        return JSON.stringify(editor.getEditorState().toJSON());
      } catch {
        return '';
      }
    });
  }, [editor, onProvideGetSerializedState]);

  // IN: HTML -> Editor
  useEffect(() => {
    const incoming = (html ?? '').trim();

    // first mount: if html is empty, do nothing
    if (!didInitRef.current) {
      didInitRef.current = true;
      if (!incoming) {
        isHydratedRef.current = true;
        return;
      }
    }

    // If parent is sending what we just exported, ignore (loop guard)
    if (incoming && incoming === lastExportedRef.current) {
      lastImportedRef.current = incoming;
      isHydratedRef.current = true;
      return;
    }

    // No change
    if (incoming === lastImportedRef.current) {
      isHydratedRef.current = true;
      return;
    }

    lastImportedRef.current = incoming;
    applyingExternalRef.current = true;

    // Handle Lexical JSON by setting editor state directly.
    if (incoming.startsWith('{"root"')) {
      try {
        const editorState = editor.parseEditorState(incoming);
        editor.setEditorState(editorState);
      } catch (e) {
        console.error('StateBridgePlugin JSON import failed:', e);
        editor.update(() => {
          const root = $getRoot();
          root.clear();
          root.append(
            $createParagraphNode().append($createTextNode(stripHtml(incoming)))
          );
        });
      }

      queueMicrotask(() => {
        applyingExternalRef.current = false;
        isHydratedRef.current = true;
      });
      return;
    }

    editor.update(() => {
      try {
        const dom = preprocessHtml(incoming);
        const nodes = $generateNodesFromDOM(editor, dom);

        const root = $getRoot();
        root.clear();

        // Important: append into root (more stable than $insertNodes here)
        root.append(...nodes);
      } catch (e) {
        console.error('StateBridgePlugin HTML import failed:', e);

        // fallback: keep editor valid
        const root = $getRoot();
        root.clear();
        root.append(
          $createParagraphNode().append($createTextNode(stripHtml(incoming)))
        );
      }
    });

    // allow exports after this update
    queueMicrotask(() => {
      applyingExternalRef.current = false;
      isHydratedRef.current = true;
    });
  }, [editor, html]);

  return <OnChangePlugin onChange={executeOnChange} />;
}

function preprocessHtml(html: string): Document {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Fix inherit sizes (your HTML has width="inherit")
  doc.querySelectorAll('img').forEach((img) => {
    if (img.getAttribute('width') === 'inherit') img.removeAttribute('width');
    if (img.getAttribute('height') === 'inherit') img.removeAttribute('height');
  });

  // If you still crash due to tables, uncomment this TEMPORARILY to confirm:
  // doc.querySelectorAll('table').forEach(t => t.remove())

  return doc;
}

function stripHtml(s: string) {
  return (s ?? '').replace(/<[^>]*>/g, '').trim();
}

export type RichTextEditorProps = {
  value?: string | null;
  onChange?: (html: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  minHeight?: number;
  onUploadFile?: (file: File) => Promise<string>;
  /** Consumer can receive a function that returns latest serialized HTML */
  onProvideGetHtml?: (getHtml: () => string) => void;
  /** Consumer can receive a function that returns latest Lexical JSON string */
  onProvideGetSerializedState?: (getSerializedState: () => string) => void;
};

export function RichTextEditor({
  value,
  onChange,
  className,
  disabled,
  placeholder = 'Start writing...',
  minHeight = 180,
  onUploadFile,
  onProvideGetHtml,
  onProvideGetSerializedState,
}: RichTextEditorProps) {
  // Convert RichTextEditorProps to EditorProps expected by the RichText component
  const editorProps: EditorProps = {
    placeholder,
    initialContent: null, // Let StateBridgePlugin handle HTML initialization
    editable: !disabled,
    storeText: onChange,
    // RichText component handles internal state, we use bridge plugin for external HTML sync
    style: cn(
      'relative outline-none text-foreground',
      !disabled ? 'p-5' : 'p-0',
      className
    ),
    onUploadFile,
    onProvideGetHtml,
    onProvideGetSerializedState,
  };

  return (
    <div className={cn(className)} style={{ minHeight }}>
      <TooltipProvider delayDuration={300}>
        <Editor {...editorProps}>
          <StateBridgePlugin
            html={value}
            onChange={onChange}
            onProvideGetHtml={onProvideGetHtml}
            onProvideGetSerializedState={onProvideGetSerializedState}
          />
        </Editor>
      </TooltipProvider>
    </div>
  );
}
